/*
 * ecom-facets-core.js  (search & collection facets, Phase 1, 2026-10-08)
 * Spec: company/ecom/design/search_facets_2026-10-08/SEARCH_FACETS_REVIEW.md (approved by Brian 2026-10-08 09:07 PT)
 *
 * Shared by the collection page (assets/ecom-collection-algolia-lazy.js), /search (sections/algolia-search.liquid)
 * and /pages/catalog (sections/algolia-collection.liquid). Exposes window.EcomFacets.
 *
 * THIS FILE NEVER TALKS TO ALGOLIA BY ITSELF. Loading it costs 0 Algolia requests; everything here either is a pure
 * function or works on InstantSearch state that the page's own (lazy) InstantSearch instance already has.
 *
 *   §1 size parsing + comparators ............ item 4 (true size order: #sizes, fractions, M-sizes, inch/mm lengths)
 *   §2 duplicate-value merge ................. item 5 (M10-1.5 + M10-1.50 -> one row "M10-1.5", refines on both)
 *   §3 facet list widget (connector) ......... items 4/5/6 (sort the FULL list then cut; local find box: never calls
 *                                              searchForFacetValues; local "Show all")
 *   §4 product families + rail order ......... items 1/3 (per-family DOM order, hidden facets, open/closed defaults)
 *   §5 Inch | Metric toggle .................. item 2
 *   §6 sort control (registry) ............... item 7 (Relevance only today; Phase 2 replicas slot into SORTS)
 *   §7 mobile filter sheet (dialog) .......... item 8
 *   §8 card pack line + $/pc ................. item 9 (rules = assets/ecom-ppp.js, the same code the PDP uses)
 *   §9 chips (current refinements) ........... merged values show/remove as one chip
 *
 * Works in Node too (module.exports) so the pure parts are unit-tested: see tests/ in the build folder.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.EcomFacets = api;
})(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  function esc(str) {
    if (str === null || str === undefined) return '';
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* =====================================================================================================
     §1 SIZE PARSING (item 4)
     Reads the SHAPE of a value, so a size nobody has stocked before sorts right the first time. Extends the
     live sizeSort (ecom-collection-algolia-lazy.js 2026-10-01) with: metric pitches written "M12x1.75" /
     "M10-1.5", metric COARSE pitch before fine (doc §5.1), unspaced inch ranges (1/2"-7/8"), unit carried
     across ranges ("1.4 - 1.8 mm"), and fraction threads without the inch mark (1/4-20).
     Groups, in the order a buyer scans: #gauges, inch, mm, M-designations. Unreadable values go last, never hidden.
  ===================================================================================================== */
  var G = { GAUGE: 0, INCH: 1, MM: 2, METRIC: 3, UNKNOWN: 9 };

  // rev 5 / 5b (SORT38): one spelling for the inch mark before parsing. \u201d, \u2033 (double prime) and '' (two
  // apostrophes) are inches; a SINGLE ' stays feet. A trailing in / in. / inch / inches after a number is inches too
  // ("1 in", "3/8 inch", "1in."). Before, 3/8'' read as 4.5" (feet) and 1\u201d / 1 inch sorted as unreadable (last).
  function inchMarks(raw) {
    return String(raw === null || raw === undefined ? '' : raw).trim()
      .replace(/[\u201d\u2033]|''|\u2019\u2019/g, '"')
      .replace(/(\d)\s*(?:in\.?|inch(?:es)?)$/i, '$1"')
      .trim();
  }
  function frac(t) {
    var m = String(t).match(/^(\d+)\/(\d+)$/);
    return m ? parseInt(m[1], 10) / parseInt(m[2], 10) : NaN;
  }
  function magnitude(t) {                       // one inch-ish quantity -> number ("1 1/4", "1-1/4", "3/4", ".25")
    t = inchMarks(t).replace(/"$/, '').trim();
    var mixed = t.match(/^(\d+)[\s-](\d+\/\d+)$/);
    if (mixed) return parseInt(mixed[1], 10) + frac(mixed[2]);
    var f = frac(t);
    if (!isNaN(f)) return f;
    if (!/^\d*\.?\d+$/.test(t)) return NaN;      // stricter than parseFloat: "1/2-13" must not read as 1
    return parseFloat(t);
  }

  function parseSize(raw) {
    var v = inchMarks(raw);
    if (!v) return { group: G.UNKNOWN, a: Infinity, b: 0 };

    // metric designation first (before the "x" split): M6, M6-1.0, M12x1.75, M10 × 1.5. A second number > 6
    // is a length ("M6x20"), not a pitch.
    var met = v.match(/^M\s*(\d+(?:\.\d+)?)(?:\s*(?:-|x|\u00d7)\s*(\d*\.?\d+))?\s*(?:mm)?$/i);
    if (met) {
      var p = met[2] ? parseFloat(met[2]) : 0;
      return { group: G.METRIC, a: parseFloat(met[1]), b: p > 0 && p <= 6 ? p : 0 };
    }

    // spaced range "#10 - #12", ".7 - .9 mm", ".051\" - .080\"": lower bound, carrying a trailing unit over
    var spaced = v.split(/\s+-\s+/);
    if (spaced.length > 1) {
      var lo = spaced[0], hiPart = spaced[spaced.length - 1];
      if (/mm$/i.test(hiPart) && !/mm$/i.test(lo)) lo += ' mm';
      else if (/"$/.test(hiPart) && !/"$/.test(lo) && !/^#/.test(lo)) lo += '"';
      return parseSize(lo);
    }

    // an expression in a single-dimension field ("#8x2 1/2\"", "1/4\"x1\"x0.050"): read the first part
    var xs = v.split(/\s*[x\u00d7]\s*/i);
    if (xs.length > 1 && xs[0].trim()) return parseSize(xs[0]);

    // decimal / inch equivalent in brackets: "#4 (0.115)"
    var brack = v.match(/^([^(]+?)\s*\([^)]*\)\s*$/);
    if (brack) return parseSize(brack[1]);

    // "#5/16" (a # on an inch size; data fault on ~170 products): read as inches
    var hashInch = v.match(/^#\s*(\d+\/\d+|\d+\s+\d+\/\d+)"?$/);
    if (hashInch) return { group: G.INCH, a: magnitude(hashInch[1]), b: 0 };

    // feet, incl. 5/32' and 12'
    if (/'$/.test(v)) {
      var ft = magnitude(v.replace(/'/g, ''));
      return { group: G.INCH, a: isNaN(ft) ? Infinity : ft * 12, b: 0 };
    }

    // millimetres: 10mm, 1.5 mm, .90 mm
    var mm = v.match(/^(\d*\.?\d+)\s*mm$/i);
    if (mm) return { group: G.MM, a: parseFloat(mm[1]), b: 0 };

    // gauge: #10, #10-24, bare 0 / 00 / 000
    var g = v.match(/^#\s*(\d+)(?:\s*-\s*(\d+))?$/) || v.match(/^(0{1,3})$/);
    if (g) return { group: G.GAUGE, a: g[1].length > 1 && /^0+$/.test(g[1]) ? -g[1].length + 1 : parseInt(g[1], 10), b: g[2] ? parseInt(g[2], 10) : 0 };

    // unspaced inch range, both ends quoted: 1/2"-7/8", 9/16"-1-1/16", 1"-2"
    var irange = v.match(/^(.+?")\s*-\s*(.+")$/);
    if (irange) return parseSize(irange[1]);

    // inch with a pitch: 1/4"-20, 1-1/2"-12, 1"-8
    var pitched = v.match(/^(.+?)"\s*-\s*(\d+(?:\.\d+)?)$/);
    if (pitched) {
      var d = magnitude(pitched[1]);
      if (!isNaN(d)) return { group: G.INCH, a: d, b: parseFloat(pitched[2]) };
    }
    // fraction thread without the inch mark: 1/4-20, 1 1/4-7
    var fpitched = v.match(/^(\d+\/\d+|\d+[\s-]\d+\/\d+)\s*-\s*(\d+)$/);
    if (fpitched) return { group: G.INCH, a: magnitude(fpitched[1]), b: parseFloat(fpitched[2]) };

    // bare decimals: ascending pair = range (.188-.250), otherwise size-pitch (4-36)
    var range = v.match(/^(\.?\d+(?:\.\d+)?)\s*-\s*(\.?\d+(?:\.\d+)?)$/);
    if (range) {
      var l = parseFloat(range[1]), h = parseFloat(range[2]);
      if (h > l) return { group: G.INCH, a: l, b: 0 };
      return { group: G.INCH, a: l, b: h };
    }

    var n = magnitude(v);
    if (!isNaN(n)) return { group: G.INCH, a: n, b: 0 };

    // last resort: a readable leading part ("#14L (1/4\")", "#6 NARROW", "1\"-8,7/8\"-9", ".406 | Thickness...")
    var lead = v.match(/^(#\s*\d+)[A-Z]+\b/i) || v.match(/^([^,|]+?)\s*[,|]/) || v.match(/^(#\s*\d+)\s+[A-Z]/i);
    if (lead && lead[1] !== v) return parseSize(lead[1]);
    return { group: G.UNKNOWN, a: Infinity, b: 0 };
  }

  function compareSize(x, y) {
    var A = parseSize(x), B = parseSize(y);
    if (A.group !== B.group) return A.group - B.group;
    if (A.a !== B.a) return A.a < B.a ? -1 : 1;
    if (A.b !== B.b) {
      // inch/gauge: TPI ascending = coarse first. Metric: pitch DESCENDING = coarse first; bare "M10" before pitches.
      if (A.group === G.METRIC) {
        if (!A.b) return -1;
        if (!B.b) return 1;
        return B.b - A.b;
      }
      return A.b - B.b;
    }
    return String(x).localeCompare(String(y), undefined, { numeric: true });
  }

  // Grade / class by strength. Rev 4 (Brian/Mira 2026-10-08 10:37 PT), replacing the QA P1 F7 order:
  //   tier 0  inch steel in strength bands, listed order inside a band:
  //             Grade 2, A307 | Grade 5, A325, A449, B7 | Grade 8, A490 | Grade 9 (L9), A354 BD
  //   tier 1  nut and washer specs as their own small group: A563 (any grade: A, B, C, DH ...), F436
  //   tier 2  metric property classes by number (4.6 ... 8.8, 10, 10.9, 12.9; nut classes 6 / 8 / 10 by number)
  //   tier 3  stainless: 18-8, B8, 303, 304, A2-70, A2-80, 316, B8M, A4-70, A4-80, 410, 416, 420
  //   tier 9  anything else (2H, Grade 36, Grade 45H, B7M, A354 BC, ...): last, A-Z
  // Variants match on the spec number, so "ASTM A325", "A325 Type 1", "A307 Grade B", "SAE J429 Grade 5", "A193 B7" and
  // "A563 Grade DH" all land in their group. Ties inside a slot (A307 Grade A / B, A563 A / B / C / DH) sort naturally.
  var SPEC = '^(?:ASTM\\s*|SAE\\s*(?:J429\\s*)?)?';
  function rx(body) { return new RegExp(SPEC + body, 'i'); }
  var STEEL_BANDS = [
    [rx('Grade\\s*2(?![\\dA-Z.])'), rx('A307(?!\\d)')],
    [rx('Grade\\s*5(?![\\dA-Z.])'), rx('A325(?!\\d)'), rx('A449(?!\\d)'), rx('(?:A193\\s*)?(?:Grade\\s*)?B7(?![\\dM])')],
    [rx('Grade\\s*8(?![\\dA-Z.])'), rx('A490(?!\\d)')],
    [rx('(?:Grade\\s*9(?![\\d.])|(?:Grade\\s*)?L-?9(?!\\d))'), rx('A354(?:\\s*Grade)?\\s*BD(?![A-Z])')]
  ];
  var NUT_WASHER = [rx('A563(?!\\d)'), rx('F436(?!\\d)')];
  // rev 6 (proposed 2026-10-10): 303 joins the A2 slot; martensitic 410 / 416 / 420 close the stainless tier instead of falling to tier 9
  var STAINLESS = [/^18-8(?!\d)/, rx('(?:Grade\\s*)?B8(?![\\dM])'), /^303(?!\d)/, /^304(?!\d)/, /^A2-70(?!\d)/i, /^A2-80(?!\d)/i, /^316(?!\d)/, rx('(?:Grade\\s*)?B8M(?!\\d)'), /^A4-70(?!\d)/i, /^A4-80(?!\d)/i, /^410(?!\d)/, /^416(?!\d)/, /^420(?!\d)/];
  function gradeRank(raw) {
    var v = String(raw === null || raw === undefined ? '' : raw).trim(), m, b, i;
    for (b = 0; b < STEEL_BANDS.length; b++) for (i = 0; i < STEEL_BANDS[b].length; i++) if (STEEL_BANDS[b][i].test(v)) return { f: 0, n: b * 10 + i };
    for (i = 0; i < NUT_WASHER.length; i++) if (NUT_WASHER[i].test(v)) return { f: 1, n: i };
    if ((m = v.match(/^(?:Property\s*)?Class\s+(\d+(?:\.\d+)?)$/i))) return { f: 2, n: parseFloat(m[1]) };
    for (i = 0; i < STAINLESS.length; i++) if (STAINLESS[i].test(v)) return { f: 3, n: i };
    return { f: 9, n: 0 };
  }
  function compareGrade(x, y) {
    var A = gradeRank(x), B = gradeRank(y);
    if (A.f !== B.f) return A.f - B.f;
    if (A.n !== B.n) return A.n < B.n ? -1 : 1;
    return String(x).localeCompare(String(y), undefined, { numeric: true });
  }

  // Thread series: fixed order (doc §5.1); unknown values after, alphabetical
  var SERIES_ORDER = ['UNC', 'UNF', 'UNEF', '8-UN', '12-UN', 'UNS', 'ISO Metric', 'Metric Coarse', 'Metric Fine'];
  function compareSeries(x, y) {
    var a = SERIES_ORDER.indexOf(x), b = SERIES_ORDER.indexOf(y);
    if (a < 0) a = 99; if (b < 0) b = 99;
    return a !== b ? a - b : String(x).localeCompare(String(y));
  }

  // items: [{label, count}] -> sorted copy
  function sortItems(items, mode) {
    var list = items.slice();
    if (mode === 'size') list.sort(function (a, b) { return compareSize(a.label, b.label); });
    else if (mode === 'grade') list.sort(function (a, b) { return compareGrade(a.label, b.label); });
    else if (mode === 'series') list.sort(function (a, b) { return compareSeries(a.label, b.label); });
    else if (mode === 'name') list.sort(function (a, b) { return String(a.label).localeCompare(String(b.label), undefined, { numeric: true }); });
    else list.sort(function (a, b) { return (b.count - a.count) || String(a.label).localeCompare(String(b.label)); }); // 'count'
    return list;
  }

  /* =====================================================================================================
     §2 DUPLICATE VALUES (item 5) - display only; every raw value stays refinable.
     Merges only format variants of the SAME value:
        M10-1.5 / M10-1.50 / M10x1.5  -> "M10-1.5"        (thread size; Mira 2026-10-08: the product-title / typed form, not "M10 × 1.5")
        M10-1.0 / M10-1               -> "M10-1"          (QA P1 F3: no trailing ".0" on the pitch)
        12mm / 12 mm                  -> "12 mm"          (lengths, OD, ID, thickness, across flats)
        .125 / 0.125 / .125" / 1/8"   -> 1/8"             (QA P1 F3: inch decimals that are EXACTLY a 64th, trailing zeros ok)
        1-1/8" / 1 1/8" / 1.125"      -> 1 1/8"           (mixed numbers: hyphen or space)
        3/8 / 3/8"                    -> 3/8"             (fraction without the inch mark); 1-1/8"-7 / 1 1/8"-7 -> 1 1/8"-7
        .156 / 0.156 / .156"          -> one row          (same number, not a 64th: shown in its most common raw spelling)
     It deliberately does NOT merge: 6" with 6mm; 15/16" with 1 5/16"; rounded decimals (.188, .312, .094 stay as typed:
     3/16 is .1875, so ".188" is a different text value, possibly a different spec); bare decimals of 1 or more with no
     inch mark ("1.5", "3.2", "5.5" in Across Flats are mm hex sizes); ranges and anything with extra text.
  ===================================================================================================== */
  function fmtPitch(n) { return String(n); }               // 1 -> "1", 1.25 -> "1.25" (F3: M10-1.0 reads M10-1)
  function gcd(a, b) { return b ? gcd(b, a % b) : a; }
  // inch quantity -> { key, label } or null. whole + num/den (den a power of 2 up to 64), optional "-TPI"
  function inchCanon(whole, num, den, tpi) {
    if (num) { var g = gcd(num, den); num /= g; den /= g; }
    var w = whole ? String(whole) : '';
    var f = num ? num + '/' + den : '';
    var size = w && f ? w + ' ' + f : (w || f);
    return { key: 'in:' + (whole || 0) + '+' + (num ? f : '0') + (tpi ? '-' + tpi : ''), label: size + '"' + (tpi ? '-' + tpi : '') };
  }
  function canonical(raw) {
    var v = String(raw === null || raw === undefined ? '' : raw).trim();
    var m = v.match(/^M\s*(\d+(?:\.\d+)?)\s*(?:-|x|\u00d7)\s*(\d*\.?\d+)$/i);
    if (m && parseFloat(m[2]) > 0 && parseFloat(m[2]) <= 6) {
      var d = parseFloat(m[1]), p = parseFloat(m[2]);
      return { key: 'M' + d + '-' + p, label: 'M' + d + '-' + fmtPitch(p) };
    }
    var mm = v.match(/^(\d*\.?\d+)\s*mm$/i);
    if (mm) { var n = parseFloat(mm[1]); return { key: n + 'mm', label: n + ' mm' }; }
    var q = inchMarks(v);   // rev 5b: same inch spellings as parseSize (3/8'' and 3/8 inch merge with 3/8")
    // fraction / mixed number: 1/8", 3/8, 1-1/8", 1 1/8", 1/2"-13, 1-1/8"-7 (whole and fraction joined by ONE hyphen or space)
    var fr = q.match(/^(?:(\d+)(?:-| +))?(\d+)\/(\d+)\s*(?:"|in\.?)?(?:\s*-\s*(\d+))?$/i);
    if (fr) {
      var fn = parseInt(fr[2], 10), fd = parseInt(fr[3], 10);
      if (fn > 0 && fn < fd && fd <= 64 && (fd & (fd - 1)) === 0) return inchCanon(fr[1] ? parseInt(fr[1], 10) : 0, fn, fd, fr[4]);
    }
    // rev 5c F4: whole inches with an inch mark: 1", 1'', 1″, 1 in, 1 inch (and 1"-8) -> one row "1"". A bare "1" is
    // NOT treated as inches (no unit). The merged row keeps every raw value in aliases, so ticking it refines on all.
    var wh = q.match(/^(\d+)\s*"(?:\s*-\s*(\d+))?$/);
    if (wh && parseInt(wh[1], 10) > 0) return inchCanon(parseInt(wh[1], 10), 0, 1, wh[2]);
    // decimal inch: .125, 0.125, .250", 1.125" (bare decimals only below 1)
    var dc = q.match(/^(\d*)\.(\d+)\s*("|in\.?)?$/i);
    if (dc) {
      var x = parseFloat((dc[1] || '0') + '.' + dc[2]);
      if (x > 0 && (dc[3] || x < 1)) {
        var w = Math.floor(x), s64 = (x - w) * 64;
        if (s64 > 0 && Math.abs(s64 - Math.round(s64)) < 1e-6) return inchCanon(w, Math.round(s64), 64, null);
        // not a 64th: still one row per NUMBER (".156" / "0.156" / ".156\"" are one value). The label stays the
        // most common raw spelling (mergeItems keeps the first, Algolia lists by count), so ".188" never reads 3/16".
        return { key: 'in:d' + x, label: v };
      }
    }
    return { key: v, label: v };
  }
  // items from the connector: [{value, label, count, isRefined}] -> merged [{key, value, label, count, isRefined, aliases:[raw values]}]
  function mergeItems(items) {
    var byKey = {}, out = [];
    items.forEach(function (it) {
      var c = canonical(it.label);
      var m = byKey[c.key];
      if (!m) {
        m = byKey[c.key] = { key: c.key, value: it.value, label: c.label, rawLabels: [it.label], count: 0, isRefined: false, aliases: [], _top: -1 };
        out.push(m);
      } else m.rawLabels.push(it.label);
      m.aliases.push(it.value);
      m.count += it.count;                       // product-level counts: one product has one value, so the sum is exact
      m.isRefined = m.isRefined || !!it.isRefined;
      if (it.count > m._top) { m._top = it.count; m.value = it.value; }
    });
    out.forEach(function (m) { delete m._top; });
    return out;
  }

  /* =====================================================================================================
     §3 FACET LIST WIDGET (items 4, 5, 6)
     A connectRefinementList renderer that replaces instantsearch.widgets.refinementList for the rail.
       - asks for the whole list (limit 1000 = maxValuesPerFacet the pages already request), merges duplicates,
         sorts the FULL list (size / grade / count / series), and only then cuts to `limit` with a local
         "Show all N" button. The old code sorted only the 12 most common values (transformItems runs after the cut).
       - find box: filters the values already loaded, in the browser. It never calls searchForFacetValues, so a
         keystroke costs 0 Algolia requests (doc §8.7).
       - a merged row refines on all of its raw values in ONE search (helper chain + one .search()).
     Markup keeps the live class names (ais-RefinementList-*, .ecom-filter-option*) so the theme CSS, the
     auto-hide rule (details.ecom-filter-group:not(:has(li:nth-child(2)))) and the chips keep working.
  ===================================================================================================== */
  function normFind(s) {
    return String(s || '').toLowerCase().replace(/(\d)\s+mm\b/g, '$1mm').replace(/\u00d7/g, '-').replace(/["\u201d\u2033]/g, '').replace(/\s*-\s*/g, '-')
      .replace(/(\d)\s*x\s*(?=\d)/g, '$1-')
      // mixed numbers: "1-1/2" and "1 1/2" both mean one and a half ("3/8-16" is left alone: no fraction after the dash)
      .replace(/(^|[^\d\/])(\d+)-(\d+\/\d+)/g, '$1$2 $3')
      .replace(/\s+/g, ' ').trim();
  }
  // Size facets (QA P1, Mira): match on SIZE BOUNDARIES. Text is normalised like the F3 merge (quotes dropped,
  // "x"/"×" -> "-", a mixed number "1-1/4" / "1 1/4" becomes ONE token "1_1/4"); a typed decimal that is an exact 64th
  // is also tried as a fraction (".25" -> "1/4", "1.5" -> "1_1/2", while "1.5" still finds the M10-1.5 pitch).
  //   - a match must START a token: not after a digit, ".", "/" or "_" (so "1/4" skips the 1/4 inside 1_1/4, "10" skips 110)
  //   - while only the nominal size is typed (no "-"), a query ending in a digit must also END the number:
  //     "M10" finds M10, M10-1.5, M10-1.25 but not M100; "1/4" finds 1/4"-20 and 1/4" but not 1/4 inside 1/4x... digits.
  //     Once a "-" is typed the tail is a prefix ("3/8-1" finds 3/8"-16 while typing).
  function normSize(s, decToFrac) {
    var t = String(s || '').toLowerCase().replace(/(\d)\s+mm\b/g, '$1mm').replace(/[\u201d\u2033"]/g, '').replace(/\u00d7/g, '-')
      .replace(/(\d)\s*x\s*(?=[\d.#m])/g, '$1-').replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ').trim();
    if (decToFrac) t = t.replace(/(^|[^\d.\/])(\d*\.\d+)(?![\d.\/])/g, function (all, pre, num) {
      var x = parseFloat(num), w = Math.floor(x), s64 = (x - w) * 64;
      if (!(s64 > 0) || Math.abs(s64 - Math.round(s64)) > 1e-6) return all;
      var n = Math.round(s64), d = 64, g = gcd(n, d); n /= g; d /= g;
      return pre + (w ? w + '_' : '') + n + '/' + d;
    });
    return t.replace(/(^|[^\d.\/])(\d+)[ -](\d+\/\d+)/g, '$1$2_$3');
  }
  // prefix = true (rev 5, N3): the typed text may be the START of a size ("M1" on the way to M12): the match must still
  // start on a size boundary, but the number may continue
  function boundaryFind(h, q, prefix) {
    var tailExact = !prefix && /\d$/.test(q) && q.indexOf('-') < 0;
    for (var at = h.indexOf(q); at !== -1; at = h.indexOf(q, at + 1)) {
      if (at > 0 && /[\d.\/_]/.test(h.charAt(at - 1))) continue;
      var nx = h.charAt(at + q.length);
      if (tailExact && nx && /[\d.\/_]/.test(nx)) continue;
      return true;
    }
    return false;
  }
  // mode 'size' (size-sorted facets) = boundary match; 'size-prefix' = boundary start, open end (N3 completions);
  // anything else (Material, Finish ...) = plain substring
  // Batch A item 7 (Brian 2026-10-10): a BARE NUMBER (digits only: no / " . - M or unit) on a size facet matches every value
  // whose leading number starts with it, in the list's size order: "5" -> 5/16", 5/8", 5", 5 1/2", 5mm (and 50mm). The leading
  // number is the digits at the very start of the value, so M5, #5 and 1 5/8" are not hits for "5". Anything else typed keeps
  // the strict boundary rule (M10 never finds M100; 1/4 never finds 1 1/4").
  function bareNumber(q) { return /^\d+$/.test(q); }
  // QA F3 (Mira): EQUALS, not starts-with: "5" -> 5/16", 5/8", 5", 5 1/2", 5mm, 5.5mm; not 50mm, not 1 5/8"
  function leadingNumberStarts(label, q) { var m = String(label || '').trim().match(/^(\d+)(?!\d)/); return !!m && Number(m[1]) === Number(q); }
  function itemMatches(item, q, mode) {
    q = String(q || '').trim();
    if (!q) return true;
    var hay = [item.label].concat(item.rawLabels || []), i;
    var prefix = mode === 'size-prefix';
    if ((mode === 'size' || prefix) && bareNumber(q)) { for (i = 0; i < hay.length; i++) if (leadingNumberStarts(hay[i], q)) return true; return false; }
    if (mode !== 'size' && !prefix) {
      var t = normFind(q);
      for (i = 0; i < hay.length; i++) if (normFind(hay[i]).indexOf(t) !== -1) return true;
      return false;
    }
    var q1 = normSize(q, false), q2 = normSize(q, true);
    for (i = 0; i < hay.length; i++) {
      if (boundaryFind(normSize(hay[i], false), q1, prefix)) return true;
      if (q2 !== q1 && boundaryFind(normSize(hay[i], true), q2, prefix)) return true;
    }
    return false;
  }

  // rev 5 (Brian via Mira): decimal hint after inch FRACTIONS on Length, Fits Screw Size (diameter_nominal_), ID and OD:
  // 3/8" (.375), 1 1/4" (1.250), 5/16" (.3125). 3 decimals, 4 when the fraction needs them; no leading zero.
  // Never on thread sizes (3/8"-16), metric values, gauges / #sizes, whole inches or decimals already in the label.
  var DEC_ATTRS = /(^|\.)(length_nominal_|diameter_nominal_|inside_diameter|outer_diameter|thickness)$/;   // rev 5c F1: + thickness
  function decimalHint(label) {
    var m = inchMarks(label).match(/^(?:(\d+)[ -])?(\d+)\/(\d+)\s*"$/);
    if (!m) return '';
    var n = parseInt(m[2], 10), d = parseInt(m[3], 10);
    if (!(n > 0 && n < d)) return '';
    var x = (m[1] ? parseInt(m[1], 10) : 0) + n / d;
    var t = x.toFixed(3);
    if (Math.abs(parseFloat(t) - x) > 1e-9) t = x.toFixed(4);
    return t.replace(/^0\./, '.');
  }
  // the hint is visual only (aria-hidden): the checkbox keeps its accessible name from the label text + count
  function decHtml(it) { return it.dec ? ' <span class="ecom-facet-dec" aria-hidden="true">(' + it.dec + ')</span>' : ''; }

  function optionHtmlEcom(it) {
    return '<label class="ecom-filter-option ' + (it.isRefined ? 'is-active' : '') + '">' +
      '<input type="checkbox" data-ef-key="' + esc(it.key) + '"' + (it.isRefined ? ' checked' : '') + ' class="ecom-facet-cb">' +
      '<span class="ecom-filter-option__check" aria-hidden="true">' +
        '<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</span>' +
      '<span class="ecom-filter-option__label">' + esc(it.label) + decHtml(it) + '</span>' +
      '<span class="ecom-filter-option__count">(' + Number(it.count).toLocaleString('en-US') + ')</span>' +
    '</label>';
  }
  // Batch C: tile picker option. A REAL checkbox (visually hidden, still focusable + announced), visible label + count,
  // icon aria-hidden. Selected = checked + navy border/tint + check badge; focus ring on :focus-visible (ecom-facets.css).
  function optionHtmlTile(it, attr) {
    var T = window.EcomTiles, ic = T ? T.iconFor(attr, it.value) : null;
    return '<label class="ecom-tile' + (ic ? '' : ' ecom-tile--text') + (it.isRefined ? ' is-active' : '') + '"' + (ic ? ' data-icon="' + esc(ic) + '"' : '') + '>' +
      '<input type="checkbox" class="ecom-tile__cb" data-ef-key="' + esc(it.key) + '"' + (it.isRefined ? ' checked' : '') + '>' +
      (ic ? '<span class="ecom-tile__ic" aria-hidden="true">' + T.svg(ic) + '</span>' : '') +
      '<span class="ecom-tile__l">' + esc(it.label) + '</span>' +
      '<span class="ecom-tile__n">' + Number(it.count).toLocaleString('en-US') + '</span>' +
      '<span class="ecom-tile__ok" aria-hidden="true"><svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>' +
    '</label>';
  }
  function optionHtmlAis(it) {               // InstantSearch default markup (catalog page CSS)
    return '<label class="ais-RefinementList-label">' +
      '<input type="checkbox" class="ais-RefinementList-checkbox" data-ef-key="' + esc(it.key) + '"' + (it.isRefined ? ' checked' : '') + '>' +
      '<span class="ais-RefinementList-labelText">' + esc(it.label) + decHtml(it) + '</span> ' +
      '<span class="ais-RefinementList-count">' + Number(it.count).toLocaleString('en-US') + '</span>' +
    '</label>';
  }

  // Toggle a (possibly merged) value in ONE search. helper = instantSearchInstance.mainIndex.getHelper()
  function refineAliases(helper, attribute, aliases, wasRefined) {
    if (!helper) return;
    aliases.forEach(function (v) {
      var on = helper.state.isDisjunctiveFacetRefined(attribute, v);
      if (wasRefined && on) helper.removeDisjunctiveFacetRefinement(attribute, v);
      if (!wasRefined && !on) helper.addDisjunctiveFacetRefinement(attribute, v);
    });
    helper.search();
  }

  // rev 5: unit-system blocks of a size-sorted list (gauges + inch | mm + M-sizes | unreadable), each in size order
  var UID = 0;
  var SYSTEMS = [{ key: 'inch', label: 'Inch' }, { key: 'metric', label: 'Metric' }, { key: 'other', label: 'Other' }];
  function systemOf(label) {
    var g = parseSize(label).group;
    return g === G.INCH || g === G.GAUGE ? 'inch' : g === G.MM || g === G.METRIC ? 'metric' : 'other';
  }
  function splitSystems(items) {
    var b = { inch: [], metric: [], other: [] };
    items.forEach(function (it) { b[systemOf(it.label)].push(it); });
    return b;
  }
  // size-sorted items -> the blocks to draw, or null when the list holds only one system (then: plain list).
  // Collapsed: limit/2 + limit/2 rows (5 + 5), a system with fewer rows gives its spare rows to the other, then to
  // "Other"; refined rows past the cut stay pinned in their own block. Expanded (showAll): every row of every block.
  function systemBlocks(items, limit, showAll) {
    var b = splitSystems(items);
    if (!b.inch.length || !b.metric.length) return null;
    var nM = Math.min(b.metric.length, Math.max(Math.ceil(limit / 2), limit - b.inch.length));
    var nI = Math.min(b.inch.length, limit - nM);
    var quota = { inch: nI, metric: nM, other: Math.min(b.other.length, Math.max(0, limit - nI - nM)) };
    return SYSTEMS.map(function (sys) {
      var all = b[sys.key], n = quota[sys.key];
      var open = showAll === true || !!(showAll && showAll[sys.key]);   // r7c: per-block expand ({inch: true}) or all (true)
      var rows = open ? all : all.slice(0, n).concat(all.slice(n).filter(function (it) { return it.isRefined; }));
      return { key: sys.key, label: sys.label, total: all.length, rows: rows, open: open, hiddenCount: all.length - Math.min(all.length, n) };
    }).filter(function (blk) { return blk.rows.length; });
  }

  /**
   * facetWidget(instantsearch, opts) -> widget
   * opts: container ('#filter-thread-size'), attribute, sort ('size'|'grade'|'count'|'series'|'name'),
   *       limit (rows before "Show all"), find (placeholder => local find box), relabel ({raw: 'Display'}),
   *       merge (default true for sort 'size'), template ('ecom' | 'ais'), noun ('sizes')
   */
  // Batch C: (i) tips on rail group headers. <button data-ecom-tip="ID" aria-expanded aria-controls> inside the <summary>
  // (preventDefault: the click must not fold the group); the panel <div id=ID role="note" hidden> is the first child after
  // the summary, so it opens the group when shown. Esc / a second click / clicking elsewhere closes it.
  function initTips(root) {
    root = root || document; if (root.__ecomTips) return; root.__ecomTips = true;
    function close(except) { [].forEach.call(document.querySelectorAll('[data-ecom-tip][aria-expanded="true"]'), function (b) { if (b === except) return; b.setAttribute('aria-expanded', 'false'); var p = document.getElementById(b.getAttribute('aria-controls')); if (p) p.hidden = true; }); }
    root.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-ecom-tip]') : null;
      if (!b) { if (!(e.target.closest && e.target.closest('.ecom-tip__panel'))) close(); return; }
      e.preventDefault(); e.stopPropagation();
      var p = document.getElementById(b.getAttribute('aria-controls')), on = b.getAttribute('aria-expanded') !== 'true';
      close(b); b.setAttribute('aria-expanded', on ? 'true' : 'false'); if (!p) return; p.hidden = !on;
      var d = b.closest('details'); if (on && d && !d.open) d.open = true;
    }, true);
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return; var b = document.querySelector('[data-ecom-tip][aria-expanded="true"]'); if (!b) return;
      close(); b.focus(); e.stopPropagation();
    });
    // keyboard: Enter / Space activate the <button> (a click), which the capture handler above stops from toggling the <summary>
  }
  if (typeof document !== 'undefined' && document.addEventListener) initTips(document);
  function facetWidget(IS, opts) {
    var o = opts || {};
    var limit = o.limit || 10;
    var merge = o.merge !== undefined ? o.merge : o.sort === 'size';
    var st = { built: false, el: null, list: null, more: null, input: null, items: [], showAll: false, showSys: {}, q: '', ism: null, uid: 'ef-sys-' + (++UID) };
    var dec = o.sort === 'size' && o.decimals !== false && DEC_ATTRS.test(o.attribute || '');

    function build(el) {
      st.el = el; el.innerHTML = '';
      el.setAttribute('data-ef-attr', o.attribute);   // F8: chips read the rail's facet name from here
      var wrap = document.createElement('div');
      wrap.className = o.template === 'ais' ? 'ais-RefinementList' : 'ais-RefinementList ecom-facet';
      if (o.find) {
        var fid = 'ef-find-' + o.attribute.replace(/[^a-z0-9]+/gi, '-');
        wrap.insertAdjacentHTML('beforeend',
          '<div class="ais-RefinementList-searchBox ecom-facet-find"><label class="ecom-visually-hidden" for="' + fid + '">' + esc(o.find) + '</label>' +
          '<input id="' + fid + '" class="ais-SearchBox-input ecom-facet-find__input" type="search" autocomplete="off" spellcheck="false" placeholder="' + esc(o.find) + '"></div>');
        st.input = wrap.querySelector('input');
        // LOCAL filtering only (item 6): no searchForFacetValues, no request of any kind.
        st.input.addEventListener('input', function () { st.q = st.input.value; draw(); });
        st.input.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });
      }
      st.list = document.createElement('div');
      wrap.appendChild(st.list);
      st.more = document.createElement('button');
      st.more.type = 'button';
      st.more.className = 'ais-RefinementList-showMore ecom-facet-more';
      st.more.addEventListener('click', function () { st.showAll = !st.showAll; draw(); });
      // r7c (Mira, iPhone drawer): "Show all N inch sizes" / "Show all N metric sizes" at the end of each block
      st.list.addEventListener('click', function (e) {
        var b = e.target.closest ? e.target.closest('[data-ef-sysmore]') : null; if (!b) return;
        // redraw AFTER the click has bubbled: theme.js's Delegate walks parentNode from the target and throws on a detached node
        var k = b.getAttribute('data-ef-sysmore'); st.showSys[k] = !st.showSys[k];
        setTimeout(function () { draw(); var nb = st.list.querySelector('[data-ef-sysmore="' + k + '"]'); if (nb) nb.focus({ preventScroll: true }); }, 0);
      });
      wrap.appendChild(st.more);
      el.appendChild(wrap);
      el.addEventListener('change', function (e) {
        var cb = e.target.closest ? e.target.closest('input[data-ef-key]') : null;
        if (!cb) return;
        var key = cb.getAttribute('data-ef-key'), it = null;
        for (var i = 0; i < st.items.length; i++) if (st.items[i].key === key) { it = st.items[i]; break; }
        if (!it || !st.ism) return;
        var tl = cb.closest('.ecom-tile'); if (tl) tl.classList.toggle('is-active', cb.checked);
        refineAliases(st.ism.mainIndex.getHelper(), o.attribute, it.aliases, it.isRefined);
      });
      st.built = true;
    }

    // rev 5c: the "(33 inch · 30 metric)" suffix is gone; the Inch / Metric headings carry the counts.
    function li(it, render) {
      return '<li class="ais-RefinementList-item' + (it.isRefined ? ' ais-RefinementList-item--selected' : '') + '">' + render(it) + '</li>';
    }
    function draw() {
      if (!st.list) return;
      var q = String(st.q || '').trim(), mode = o.sort === 'size' ? 'size' : 'text';
      var matches = st.items.filter(function (it) { return itemMatches(it, q, mode); });
      // N3 (rev 5): nothing matches on a size boundary, but the text is the start of a size in the list ("M1" on the way
      // to M12): list the values that continue it from a size boundary. "No match" only when no value can complete it.
      // "M10" still never finds M100 while M10 itself is in the list, and "1/4" never finds 1 1/4".
      if (q && mode === 'size' && !matches.length) matches = st.items.filter(function (it) { return itemMatches(it, q, 'size-prefix'); });
      var tiles = !!(o.tiles && window.EcomTiles);
      var render = tiles ? function (it) { return optionHtmlTile(it, o.attribute); } : o.template === 'ais' ? optionHtmlAis : optionHtmlEcom;
      var blocks = !q && o.sort === 'size' ? systemBlocks(matches, limit, st.showAll || st.showSys) : null;
      var split = !!blocks;
      var html = '';
      if (split) {
        // rev 5 (BUG_SHOWMORE): the size sort keeps inch and metric apart, so a flat first-10 cut showed inch only and
        // "Show all" jumped from 6" to 8 mm unexplained. Both systems now show under small headings (systemBlocks):
        // collapsed 5 + 5, expanded = the full blocks with counts, unreadable values last under "Other".
        // One <ul> per block, named by its heading for screen readers.
        html = '<div class="ecom-facet-sys-wrap">' + blocks.map(function (blk) {
          var hid = st.uid + '-' + blk.key;
          return '<p class="ecom-facet-sys" id="' + hid + '">' + blk.label + (st.showAll ? ' (' + blk.total.toLocaleString('en-US') + ')' : '') + '</p>' +
            '<ul class="ais-RefinementList-list ecom-facet-sys-list" aria-labelledby="' + hid + '">' + blk.rows.map(function (it) { return li(it, render); }).join('') + '</ul>' +
            (blk.hiddenCount > 0 && !st.showAll ? '<button type="button" class="ais-RefinementList-showMore ecom-facet-more ecom-facet-sysmore" data-ef-sysmore="' + blk.key + '" aria-expanded="' + blk.open + '">' +
              (blk.open ? 'Show fewer ' + blk.label.toLowerCase() + ' ' + (o.noun || 'values') : 'Show all ' + blk.total.toLocaleString('en-US') + ' ' + blk.label.toLowerCase() + ' ' + (o.noun || 'values')) + '</button>' : '');
        }).join('') + '</div>';
      } else {
        var cut = (st.showAll || q) ? matches : matches.slice(0, limit);
        // a refined value is never hidden behind "Show all"
        if (!st.showAll && !q) matches.slice(limit).forEach(function (it) { if (it.isRefined) cut.push(it); });
        html = cut.length ? '<ul class="ais-RefinementList-list' + (tiles ? ' ecom-tiles' : '') + '">' + cut.map(function (it) { return li(it, render); }).join('') + '</ul>' : '';
      }
      st.list.innerHTML = html || (q ? '<p class="ecom-facet-none">No match</p>' : '');
      var extra = matches.length - limit;
      st.more.hidden = !!q || extra <= 0 || split;   // r7c: a split list uses the per-block buttons
      st.more.textContent = st.showAll ? 'Show less' : 'Show all ' + matches.length + (o.noun ? ' ' + o.noun : '');
      st.list.parentNode.classList.toggle('is-expanded', !!st.showAll);
      st.list.parentNode.classList.toggle('is-split', split);
      st.more.setAttribute('aria-expanded', st.showAll ? 'true' : 'false');
      if (st.input) st.input.parentNode.hidden = st.items.length <= limit;   // no find box on short lists
      // keep the group on screen while the shopper types: the theme hides groups with < 2 options (CSS :has),
      // which would otherwise drop the box (and its focus) as soon as the query matches 0 or 1 values.
      // rev 5: the same for a split list (1 inch + 1 metric value = two lists of one <li> each)
      var grp = st.el && st.el.closest ? st.el.closest('details') : null;
      if (grp) {
        if (q) grp.setAttribute('data-ef-searching', ''); else grp.removeAttribute('data-ef-searching');
        if (split) grp.setAttribute('data-ef-split', ''); else grp.removeAttribute('data-ef-split');
      }
    }

    var connector = IS.connectors.connectRefinementList(function (r) {
      var el = document.querySelector(o.container);
      if (!el) return;
      if (!st.built || st.el !== el) build(el);
      st.ism = r.instantSearchInstance;
      var items = (r.items || []).map(function (it) {
        var label = o.relabel && o.relabel[it.label] ? o.relabel[it.label] : it.label;
        return { value: it.value, label: label, count: it.count, isRefined: it.isRefined };
      });
      items = merge ? mergeItems(items)
                    : items.map(function (it) { return { key: it.value, value: it.value, label: it.label, rawLabels: [it.label], count: it.count, isRefined: it.isRefined, aliases: [it.value] }; });
      st.items = sortItems(items, o.sort || 'count');
      if (dec) st.items.forEach(function (it) { it.dec = decimalHint(it.label); });
      draw();
    });
    return connector({ attribute: o.attribute, limit: 1000, operator: 'or', sortBy: ['count:desc', 'name:asc'] });
  }

  /* =====================================================================================================
     §4 PRODUCT FAMILIES + RAIL ORDER (items 1 and 3; doc §4)
     Family = the family of the product type that holds >= 80% of hits (doc §4 "Choosing the family"),
     else Mixed. If the buyer has picked product types and they all belong to one family, that family.
     No Product Type facet in the results (e.g. before the first answer): a collection-handle hint, else Mixed.
     Group keys are the ids of the existing rail containers without "filter-" (filter-thread-size -> thread-size).
     'pack' is reserved: options.package_quantity is not faceted in Algolia yet (needs an index setting = Phase 2).
  ===================================================================================================== */
  var TYPE_FAMILY = {};
  (function () {
    var F = {
      bolts: ['Anchor Bolts', 'Button Head Cap Screws', 'Carriage Bolts', 'Concrete Screws', 'Deck & Construction Screws', 'Draw Bolts',
        'Drive Screws', 'Drywall Screws', 'Elevator Bolts', 'Eye Bolts', 'Flange Bolts', 'Flat Head Cap Screws', 'Hanger Bolts',
        'Heavy Hex Bolts', 'Hex Bolts', 'Hex Cap Screws', 'Jack Screws', 'Lag Bolts', 'Low Head Cap Screws', 'Machine Bolts',
        'Machine Screws', 'Plow Bolts', 'Security Screws', 'Self-Drilling Screws', 'Self-Tapping Screws', 'SEMS Screws', 'Set Screws',
        'Shoulder Screws', 'Socket Head Cap Screws', 'Square Head Bolts', 'Step Bolts', 'Tap Bolts', 'Thread-Cutting Screws',
        'Thread-Forming Screws', 'Thumb & Wing Screws', 'Timber Bolts', 'Track Bolts', 'Weld Screws', 'Wood Screws'],
      nuts: ['12-Point Nuts', 'Acorn (Cap) Nuts', 'All-Metal Lock Nuts', 'Barrel Nuts', 'Cage Nuts', 'Castle Nuts', 'Conduit Nuts',
        'Coupling Nuts', 'Flange Nuts', 'Heavy Hex Nuts', 'Hex Nuts', 'Hex Weld Nuts', 'Jam Nuts', 'Keps Nuts', 'Knurled Nuts',
        'Lock Nuts - All Metal', 'Nylon Insert Lock Nuts', 'Propell Nuts', 'Round Base Weld Nuts', 'Self-Clinching Nuts',
        'Slotted Hex Nuts', 'Square Nuts', 'Square Weld Nuts', 'Tab / Spot Weld Nuts', 'Tee Nuts', 'Wheel Nuts', 'Wing Nuts'],
      washers: ['Back-Up Washers', 'Finishing Washers', 'Flat Washers', 'Lock Washers', 'MS Washers', 'Plate Washers', 'Sealing Washers',
        'Split Lock Washers', 'Square Washers', 'Structural Washers'],
      rod: ['Threaded Rod (All Thread)', 'B7 Studs', 'Weld Studs & CD Studs']
      // everything else (anchors, rivets, rivet nuts, pins, clips, rings, spacers, standoffs, tools, chemicals...) -> 'other'
    };
    Object.keys(F).forEach(function (fam) { F[fam].forEach(function (t) { TYPE_FAMILY[t.toLowerCase()] = fam; }); });
  })();
  function familyOfType(t) { return TYPE_FAMILY[String(t || '').toLowerCase()] || 'other'; }

  // Collection-handle hint, used only when the Product Type facet is absent
  function familyFromHandle(handle) {
    var h = String(handle || '').toLowerCase();
    if (!h || h === 'all') return null;
    if (/washer/.test(h)) return 'washers';
    if (/(^|-)nuts?($|-)/.test(h)) return 'nuts';
    if (/threaded-rod|all-thread|(^|-)studs?($|-)/.test(h)) return 'rod';
    if (/anchor|rivet|(^|-)pins?($|-)|clip|retaining|spacer|standoff|insert/.test(h)) return 'other';
    if (/bolt|screw/.test(h)) return 'bolts';
    return null;
  }

  /**
   * detectFamily({ types: [{name,count}] (product_type facet, current results), refinedTypes: [..], handle })
   * -> { family, mixedTypes (bool: more than one type in the results), by }
   */
  function detectFamily(o) {
    var types = (o && o.types) || [], refined = (o && o.refinedTypes) || [];
    if (refined.length) {
      var fams = {}; refined.forEach(function (t) { fams[familyOfType(t)] = 1; });
      var k = Object.keys(fams);
      if (k.length === 1) return { family: k[0], mixedTypes: refined.length > 1, by: 'refined-type' };
      return { family: 'mixed', mixedTypes: true, by: 'refined-types' };
    }
    if (types.length) {
      var total = 0, top = types[0];
      types.forEach(function (t) { total += t.count; if (t.count > top.count) top = t; });
      if (total > 0 && top.count / total >= 0.8) return { family: familyOfType(top.name), mixedTypes: types.length > 1, by: 'type>=80%' };
      return { family: 'mixed', mixedTypes: true, by: 'types<80%' };
    }
    var h = familyFromHandle(o && o.handle);
    return h ? { family: h, mixedTypes: false, by: 'handle' } : { family: 'mixed', mixedTypes: true, by: 'default' };
  }

  // Doc §4 table. order = rail order; open = also open on mobile (◆); desktopOpen = how many leading visible groups
  // are open on desktop (doc: first 5 on single-family pages, Product Type + next 3 on mixed pages).
  // Groups not listed and not hidden keep their live relative order after the listed ones; Made in USA is always last.
  var GLOBAL_HIDE = ['diameter', 'country-of-origin', 'rohs', 'measurement-system'];   // item 3 (Measurement System = toggle)
  var FAMILIES = {
    bolts: {
      order: ['product-type', 'thread-size', 'length', 'material', 'grade', 'finish', 'thread-series', 'thread-coverage', 'head', 'drive', 'point-type', 'pack', 'standard'],
      mobileOpen: ['product-type', 'thread-size', 'length'],
      hide: ['threading', 'alloy-type', 'width-across-flats', 'nut-type', 'washer-type', 'sems-type', 'rivet-size', 'mandrel-material', 'grip-range'],
      desktopOpen: 5
    },
    nuts: {
      order: ['nut-type', 'thread-size', 'material', 'grade', 'finish', 'thread-series', 'special-feature', 'width-across-flats', 'thickness', 'thread-direction', 'pack', 'standard'],
      mobileOpen: ['nut-type', 'thread-size'],
      // Mira 2026-10-08: ONE "Thread Series" facet right after Finish, Alloy / Type hidden. The attribute is
      // meta.custom.thread_series (UNC / UNF / ISO Metric / 8-UN / 12-UN / UNS, 98.8% of hex-nuts) rather than
      // Threading = meta.custom.thread_type (only Coarse / Fine, 93.9%, and implied by the series), so Threading is hidden.
      hide: ['length', 'head', 'drive', 'point-type', 'thread-coverage', 'threading', 'alloy-type'],
      labels: { 'thread-series': 'Thread Series' },
      desktopOpen: 5
    },
    washers: {
      order: ['washer-type', 'diameter', 'material', 'finish', 'inside-diameter', 'outer-diameter', 'thickness', 'grade', 'pack', 'standard'],
      mobileOpen: ['washer-type', 'diameter'],
      // QA P1 F4: Alloy / Type hidden on washers too (doc §4: merged into Material)
      hide: ['thread-size', 'length', 'thread-series', 'threading', 'head', 'drive', 'width-across-flats', 'thread-direction', 'alloy-type'],
      show: ['diameter'],                                    // diameter_nominal_ is the washer's size facet here
      labels: { diameter: 'Fits Screw Size' },               // doc §4 washers row
      desktopOpen: 5
    },
    rod: {
      order: ['thread-size', 'length', 'material', 'grade', 'finish', 'thread-series', 'thread-direction', 'pack', 'standard'],
      mobileOpen: ['thread-size', 'length'],
      hide: ['head', 'drive', 'point-type', 'thread-coverage'],
      desktopOpen: 5
    },
    other: {
      order: ['product-type', 'rivet-size', 'grip-range', 'diameter', 'length', 'material', 'finish', 'mandrel-material', 'body-style', 'thread-action', 'tapping-type', 'pack', 'standard'],
      mobileOpen: ['product-type', 'rivet-size', 'grip-range', 'diameter'],
      hide: [],
      show: ['diameter'],
      desktopOpen: 5
    },
    mixed: {
      // QA P1 F1 (2026-10-08): Thread Series (meta.custom.thread_series: UNC / UNF / ISO Metric ...) right after Finish,
      // as on bolts and nuts. Threading (thread_type, Coarse / Fine only) stays hidden: one thread facet, not two.
      order: ['product-type', 'thread-size', 'length', 'material', 'grade', 'finish', 'thread-series', 'head', 'drive', 'standard'],
      mobileOpen: ['product-type', 'thread-size'],
      labels: { 'thread-series': 'Thread Series' },
      // "Everything family-specific until a type is picked"
      hide: ['threading', 'alloy-type', 'thread-coverage', 'point-type', 'nut-type', 'washer-type', 'sems-type', 'special-feature',
        'width-across-flats', 'thickness', 'thread-direction', 'inside-diameter', 'outer-diameter', 'rivet-size', 'grip-range',
        'mandrel-material', 'body-style', 'thread-action', 'tapping-type', 'body-material'],
      desktopOpen: 4
    }
  };
  var LAST = ['made-in-usa'];

  // Pure: which groups are hidden and in what order, for a family. groupKeys = keys in live DOM order.
  function railPlan(family, groupKeys, opt) {
    var F = FAMILIES[family] || FAMILIES.mixed;
    var showPT = !opt || opt.mixedTypes !== false || family === 'mixed' || family === 'other';
    var hidden = {};
    GLOBAL_HIDE.concat(F.hide).forEach(function (k) { hidden[k] = true; });
    (F.show || []).forEach(function (k) { delete hidden[k]; });
    if (!showPT) hidden['product-type'] = true;            // bolts etc.: Product Type only when results are mixed
    var present = {}; groupKeys.forEach(function (k) { present[k] = true; });
    var order = [];
    F.order.forEach(function (k) { if (present[k] && order.indexOf(k) < 0) order.push(k); });
    groupKeys.forEach(function (k) { if (order.indexOf(k) < 0 && LAST.indexOf(k) < 0) order.push(k); });
    LAST.forEach(function (k) { if (present[k]) order.push(k); });
    return { order: order, hidden: hidden, mobileOpen: F.mobileOpen, desktopOpen: F.desktopOpen, labels: F.labels || {} };
  }

  /**
   * Rail controller. Call rail.apply(results, helperState) after each render.
   *  - moves the .ecom-filter-group nodes into family order (DOM order, so Tab order = visual order; replaces the
   *    CSS `order` block that only reordered visually),
   *  - hides the family's/global hidden groups (a hidden group that holds a selected value stays visible),
   *  - sets open/closed defaults when the family changes, never overriding a group the shopper toggled.
   */
  function Rail(form, cfg) {
    this.form = form; this.cfg = cfg || {}; this.family = null; this.touched = {};
    var self = this;
    if (form) form.addEventListener('click', function (e) {
      var s = e.target.closest ? e.target.closest('summary.ecom-filter-group__header') : null;
      if (s && s.parentNode) self.touched[keyOf(s.parentNode)] = true;
    });
  }
  function keyOf(group) {
    var c = group.querySelector('.ecom-filter-group__options[id^="filter-"]');
    return c ? c.id.replace(/^filter-/, '') : '';
  }
  Rail.prototype.groups = function () {
    if (!this.form) return [];
    return Array.prototype.filter.call(this.form.children, function (n) { return n.classList && n.classList.contains('ecom-filter-group'); });
  };
  Rail.prototype.apply = function (results, state) {
    if (!this.form || !results) return null;
    var types = [], refinedTypes = [];
    try { types = (results.getFacetValues('product_type', { sortBy: ['count:desc'] }) || []).map(function (t) { return { name: t.name, count: t.count }; }); } catch (e) { types = []; }
    try { refinedTypes = state ? state.getDisjunctiveRefinements('product_type') : []; } catch (e) { refinedTypes = []; }
    var fam = detectFamily({ types: types, refinedTypes: refinedTypes, handle: this.cfg.handle });
    var groups = this.groups(), byKey = {};
    groups.forEach(function (g) { var k = keyOf(g); if (k) byKey[k] = g; });
    var plan = railPlan(fam.family, groups.map(keyOf).filter(Boolean), fam);
    var form = this.form;
    plan.order.forEach(function (k) { if (byKey[k]) form.appendChild(byKey[k]); });   // DOM order
    var visible = [];
    plan.order.forEach(function (k) {
      var g = byKey[k]; if (!g) return;
      var hasChecked = !!g.querySelector('input:checked');
      var hide = !!plan.hidden[k] && !hasChecked;
      g.hidden = hide;
      g.classList.toggle('is-family-hidden', hide);
      var lab = g.querySelector('.ecom-filter-group__label');
      if (lab) {
        if (!lab.hasAttribute('data-ef-label')) lab.setAttribute('data-ef-label', lab.textContent);
        lab.textContent = plan.labels[k] || lab.getAttribute('data-ef-label');
      }
      if (!hide && g.querySelectorAll('li').length >= 2 || hasChecked) visible.push(k);
    });
    var famKey = fam.family + (plan.hidden['product-type'] ? '' : '+pt');
    if (famKey !== this.family) {                       // open/closed defaults only when the family changes
      var mobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 1024px)').matches;
      var self = this;
      visible.forEach(function (k, i) {
        if (self.touched[k]) return;
        var open = mobile ? plan.mobileOpen.indexOf(k) >= 0 : i < plan.desktopOpen;
        if (byKey[k].querySelector('input:checked')) open = true;
        byKey[k].open = open;
      });
      this.family = famKey;
    }
    if (form.setAttribute) form.setAttribute('data-ef-family', fam.family);
    return { family: fam, plan: plan, visible: visible };
  };

  /* =====================================================================================================
     §5 INCH | METRIC TOGGLE (item 2)
     Segmented control above the rail, bound to meta.custom.measurement_system (replaces that facet group).
     One value at a time; pressing the active one again shows both. Hidden when the results hold only one
     system and nothing is selected, unless both systems were seen earlier on this page (then it stays put, with
     the empty side disabled, so the rail does not jump when e.g. 1/2"-13 is picked). Filters the results and therefore every size list (disjunctive facets).
  ===================================================================================================== */
  function unitToggle(IS, opts) {
    var o = opts || {};
    var attribute = o.attribute || 'meta.custom.measurement_system';
    var VALUES = ['Inch', 'Metric'];
    var st = { el: null, ism: null, items: [], seenBoth: false };
    function draw() {
      var el = st.el; if (!el) return;
      var by = {}; st.items.forEach(function (it) { by[it.label] = it; });
      var anyRefined = st.items.some(function (it) { return it.isRefined; });
      var present = VALUES.filter(function (v) { return by[v]; });
      if (present.length >= 2) st.seenBoth = true;
      el.hidden = present.length < 2 && !anyRefined && !st.seenBoth;
      el.innerHTML = '<div class="ecom-unit-toggle" role="group" aria-label="Measurement system">' + VALUES.map(function (v) {
        var it = by[v], on = !!(it && it.isRefined);
        return '<button type="button" class="ecom-unit-toggle__btn' + (on ? ' is-active' : '') + '" data-unit="' + v + '" aria-pressed="' + on + '"' +
          (it ? ' title="' + Number(it.count).toLocaleString('en-US') + ' products"' : ' disabled') + '>' + v + '</button>';
      }).join('') + '</div>';
    }
    var connector = IS.connectors.connectRefinementList(function (r) {
      st.el = document.querySelector(o.container);
      if (!st.el) return;
      st.ism = r.instantSearchInstance;
      st.items = r.items || [];
      if (!st.el.__efBound) {
        st.el.__efBound = true;
        st.el.addEventListener('click', function (e) {
          var b = e.target.closest ? e.target.closest('[data-unit]') : null;
          if (!b || b.disabled || !st.ism) return;
          var h = st.ism.mainIndex.getHelper(), want = b.getAttribute('data-unit');
          var wasOn = h.state.isDisjunctiveFacetRefined(attribute, want);
          h.state.getDisjunctiveRefinements(attribute).forEach(function (v) { h.removeDisjunctiveFacetRefinement(attribute, v); });
          if (!wasOn) h.addDisjunctiveFacetRefinement(attribute, want);
          h.search();                                     // one request per press
        });
      }
      draw();
    });
    return connector({ attribute: attribute, limit: 10, operator: 'or' });
  }

  /* =====================================================================================================
     §6 SORT CONTROL (item 7)
     Only orders that exist today. shopify_products has NO usable sort replicas (shopify_products_recently_ordered_count_desc
     answers 404), so the Algolia-backed list is Relevance only. Phase 2 (virtual replicas for Size and Price, doc §8.10)
     = add entries to SORTS below; the control, the URL routing (InstantSearch sortBy) and the mobile pills pick them up.
     Collections keep the Shopify sort <select> (Best selling / Price / Name / Newest) for the server-rendered grid;
     this control replaces it only once Algolia has taken over the grid.
  ===================================================================================================== */
  var SORTS = [
    { id: 'relevance', label: 'Relevance', index: 'shopify_products' }
    // Phase 2 (DO NOT enable until Wes confirms the virtual replicas exist; a missing index = failed search):
    // { id: 'size',      label: 'Size (small \u2192 large)', index: 'shopify_products_size_asc' },
    // { id: 'price_asc', label: 'Price: low to high',       index: 'shopify_products_price_asc' },
    // { id: 'price_desc',label: 'Price: high to low',       index: 'shopify_products_price_desc' }
  ];
  function sortControl(IS, opts) {
    var o = opts || {};
    var items = SORTS.map(function (s) { return { label: s.label, value: s.index }; });
    var connector = IS.connectors.connectSortBy(function (r) {
      var sel = o.select && document.querySelector(o.select);
      if (sel) {
        if (!sel.__efBound) {
          sel.__efBound = true;
          sel.addEventListener('change', function () { if (sel.__efRefine) sel.__efRefine(sel.value); });
        }
        sel.__efRefine = r.refine;
        // the section renders the same options server-side (first paint); rewrite only when they differ
        var so = r.options.map(function (op) { return op.value + '|' + op.label; }).join(',');
        var sh = Array.prototype.map.call(sel.options, function (op) { return op.value + '|' + op.textContent; }).join(',');
        if (so !== sh) sel.innerHTML = r.options.map(function (op) {
          return '<option value="' + esc(op.value) + '"' + (op.value === r.currentRefinement ? ' selected' : '') + '>' + esc(op.label) + '</option>';
        }).join('');
        if (sel.value !== r.currentRefinement) sel.value = r.currentRefinement;
      }
      var pills = o.pills && document.querySelector(o.pills);
      if (pills) {
        if (!pills.__efBound) {
          pills.__efBound = true;
          pills.addEventListener('click', function (e) {
            var b = e.target.closest ? e.target.closest('[data-sort]') : null;
            if (b && pills.__efRefine) pills.__efRefine(b.getAttribute('data-sort'));
          });
        }
        pills.__efRefine = r.refine;
        var ph = r.options.map(function (op) {
          var on = op.value === r.currentRefinement;
          return '<button type="button" class="ecom-sort-pill' + (on ? ' is-active' : '') + '" data-sort="' + esc(op.value) + '" aria-pressed="' + on + '">' + esc(op.label) + '</button>';
        }).join('');
        if (pills.innerHTML !== ph) pills.innerHTML = ph;
      }
    });
    return connector({ items: items });
  }

  /* =====================================================================================================
     §7 MOBILE FILTER SHEET (item 8; doc §7)
     Reuses the live drawer (#filters-sidebar). At <= 1024px, while open it is a modal dialog:
     role="dialog", aria-modal="true", aria-labelledby = the "Filter Products" title; focus moves in, Tab is
     trapped, Esc closes, focus returns to the button that opened it. On desktop the same aside is a plain rail
     (no dialog role). The footer holds Clear all + "Show N products" (live count; tap closes the sheet).
  ===================================================================================================== */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  function isMobile() { return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 1024px)').matches; }
  function drawer(opts) {
    var o = opts || {};
    var sheet = o.sheet, backdrop = o.backdrop, openers = o.openers || [], closeBtn = o.closeBtn;
    var lastOpener = null, isOpen = false;
    if (!sheet) return { open: function () {}, close: function () {}, isOpen: function () { return false; } };
    function visibleFocusables() {
      return Array.prototype.filter.call(sheet.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }
    function onKey(e) {
      if (!isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var f = visibleFocusables(); if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || !sheet.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !sheet.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
    }
    function open(opener) {
      if (o.onOpen) o.onOpen();
      lastOpener = opener || document.activeElement;
      sheet.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      if (isMobile()) {
        sheet.setAttribute('role', 'dialog');
        sheet.setAttribute('aria-modal', 'true');
        if (o.labelledBy) sheet.setAttribute('aria-labelledby', o.labelledBy);
      }
      openers.forEach(function (b) { if (b) b.setAttribute('aria-expanded', 'true'); });
      isOpen = true;
      document.addEventListener('keydown', onKey, true);
      setTimeout(function () { (closeBtn || visibleFocusables()[0] || sheet).focus(); }, 0);
    }
    function close() {
      if (!isOpen) { sheet.classList.remove('is-open'); return; }
      isOpen = false;
      sheet.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-visible');
      document.body.style.overflow = '';
      sheet.removeAttribute('role'); sheet.removeAttribute('aria-modal'); sheet.removeAttribute('aria-labelledby');
      openers.forEach(function (b) { if (b) b.setAttribute('aria-expanded', 'false'); });
      document.removeEventListener('keydown', onKey, true);
      var back = lastOpener && document.contains(lastOpener) ? lastOpener : openers[0];
      if (back && back.focus) back.focus();
    }
    openers.forEach(function (b) {
      if (!b) return;
      b.setAttribute('aria-expanded', 'false');
      if (sheet.id) b.setAttribute('aria-controls', sheet.id);
      b.addEventListener('click', function () { open(b); });
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
    if (typeof window !== 'undefined' && window.matchMedia) {
      var mq = window.matchMedia('(max-width: 1024px)');
      var onMq = function () { if (!mq.matches && isOpen) close(); };
      if (mq.addEventListener) mq.addEventListener('change', onMq); else if (mq.addListener) mq.addListener(onMq);
    }
    return { open: open, close: close, isOpen: function () { return isOpen; } };
  }
  // "Show 856 products" / "Show 27,666 results"
  function showCountLabel(n, noun) {
    if (typeof n !== 'number' || !isFinite(n)) return 'Show ' + (noun || 'results');
    return 'Show ' + n.toLocaleString('en-US') + ' ' + (noun || 'results');
  }

  /* =====================================================================================================
     §8 CARD PACK LINE + $/pc (item 9)
     Names the pack of the variant the card represents (Algolia: options.package_quantity of the hit; server-rendered
     card: the first available variant, i.e. the one its Add to Cart adds) and its price. The unit price is decided by
     window.EcomPPP.decide (assets/ecom-ppp.js + ecom-ppp-data.js): the exact rules the PDP uses (Spock D1-D3):
       hidden for weight-sold lines, kits/sets, tools & chemicals, q3 outlier products, "Each" and 1-pc packs;
       threaded rod shows $/ft when the stick length is known, else nothing. No rules loaded = nothing shown.
  ===================================================================================================== */
  function money(n) {
    var v = Number(n);
    if (!isFinite(v)) return '';
    return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function packLabelOfHit(hit) {
    var l = (hit && hit.options && hit.options.package_quantity) || (hit && (hit.option1 || hit.variant_title)) || '';
    return /^default title$/i.test(l) ? '' : l;
  }
  function packLineHtml(hit) {
    var label = packLabelOfHit(hit);
    if (!label || hit.price === undefined || hit.price === null) return '';
    return '<div class="ae-pack" data-ppp-product="' + esc(hit.id) + '" data-ppp-variant="' + esc(hit.objectID) + '" data-ppp-price="' +
      esc(Math.round(Number(hit.price) * 100)) + '" data-ppp-label="' + esc(label) + '">' +
      '<span class="ae-pack__name">' + esc(label) + '</span> ' +
      '<span class="ae-pack__price">' + esc(money(hit.price)) + '</span>' +
      '<span class="ae-pack__ppp" hidden></span>' +
    '</div>';
  }
  // Fill every .ae-pack[data-ppp-variant] in scope that has not been decided yet. Fail closed.
  function fillPPP(scope) {
    var P = typeof window !== 'undefined' && window.EcomPPP;
    if (!P || !scope || !scope.querySelectorAll) return 0;
    var n = 0;
    Array.prototype.forEach.call(scope.querySelectorAll('.ae-pack[data-ppp-variant]:not([data-ppp-done])'), function (el) {
      el.setAttribute('data-ppp-done', '1');
      var out = el.querySelector('.ae-pack__ppp');
      if (!out) return;
      if (el.getAttribute('data-ppp-hold') === '1') { out.hidden = true; el.setAttribute('data-ppp-reason', 'pack_qty_mismatch'); return; }
      var d;
      try {
        d = P.decide({
          productId: el.getAttribute('data-ppp-product'), variantId: el.getAttribute('data-ppp-variant'),
          priceCents: parseInt(el.getAttribute('data-ppp-price'), 10), label: el.getAttribute('data-ppp-label'),
          piecesMf: el.getAttribute('data-ppp-mf') || null, ruleV: el.getAttribute('data-ppp-rule-v') || '',
          ruleP: el.getAttribute('data-ppp-rule-p') || '', stickFtMf: el.getAttribute('data-ppp-stick-ft') || null
        });
      } catch (e) { d = { mode: 'hide', reason: 'error' }; }
      el.setAttribute('data-ppp-reason', d.reason || '');
      if (d.mode === 'hide' || !d.text) { out.hidden = true; return; }
      out.textContent = d.text + '/' + d.unit;
      out.hidden = false; n++;
    });
    return n;
  }

  /* =====================================================================================================
     §9 CHIPS: currentRefinements transformItems. Merged values shown and removed as ONE chip (removing it clears
     every raw alias in one search). QA P1 F8: each chip carries its facet name on screen, as in the mockup
     ("Thread size: 1/2\"-13 ×"). The name is the rail's own label for that facet (so washers say "Fits screw size",
     nuts "Thread series"), in sentence case, else names[attribute], else the attribute in words.
  ===================================================================================================== */
  function sentence(s) {
    return String(s || '').trim().split(/\s+/).map(function (w, i) {
      if (i === 0 || /[A-Z].*[A-Z]|\d/.test(w)) return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w;   // keep USA, RoHS, 18-8
      return w.toLowerCase();
    }).join(' ');
  }
  function railName(attribute) {
    if (typeof document === 'undefined') return '';
    var el = document.querySelector('[data-ef-attr="' + attribute + '"]');
    var grp = el && el.closest ? el.closest('details') : null;
    // 2026-10-10 PM: prefer the label span (the summary also holds the Batch C info-tip button, whose "i" leaked into chips:
    // "Thread sizei: 2\"-4"); and never read tip buttons / panels as label text.
    var lab = grp ? (grp.querySelector('.ecom-filter-group__label') || grp.querySelector('summary')) : null;
    if (!lab) return '';
    var c = lab.cloneNode(true);
    Array.prototype.forEach.call(c.querySelectorAll('[data-ecom-tip], .ecom-tip, .ecom-tip__panel'), function (x) { x.parentNode.removeChild(x); });
    return c.textContent.replace(/\(\d[\d,]*\)\s*$/, '').trim();
  }
  // rev 5b (SORT38): chips follow the rail, not the click order. Inside a facet, size facets (mergeAttrs) sort with
  // compareSize and Grade with compareGrade, on the value WITHOUT the "Length: " prefix; facets follow the order of
  // their rail containers on the page ([data-ef-attr], which Rail.apply keeps in rail order). Display only: refine()
  // and the URL still carry the raw values in state order. 0 requests.
  function railRank() {
    var rank = {};
    if (typeof document === 'undefined') return rank;
    Array.prototype.forEach.call(document.querySelectorAll('[data-ef-attr]'), function (el, i) {
      var a = el.getAttribute('data-ef-attr'); if (!(a in rank)) rank[a] = i;
    });
    return rank;
  }
  function chipsTransform(names, mergeAttrs, getHelper) {
    return function (items) {
      var out = items.map(function (item) {
        var out = Object.assign({}, item);
        var short = String(item.attribute || '').replace(/^meta\.custom\./, '');
        var name = names ? (railName(item.attribute) || names[short] || short.replace(/_+$/, '').replace(/_/g, ' ')) : '';
        name = sentence(name);
        if (name) out.label = name;
        var prefix = name ? name + ': ' : '';
        var merging = mergeAttrs && mergeAttrs.indexOf(short) >= 0;
        var groups = {}, refs = [];
        item.refinements.forEach(function (r) {
          var c = merging ? canonical(r.label) : { key: r.value, label: r.label };
          if (!groups[c.key]) { groups[c.key] = Object.assign({}, r, { label: prefix + c.label, __raw: c.label, __aliases: [] }); refs.push(groups[c.key]); }
          groups[c.key].__aliases.push(r.value);
        });
        if (merging) refs.sort(function (a, b) { return compareSize(a.__raw, b.__raw); });
        else if (short === 'grade') refs.sort(function (a, b) { return compareGrade(a.__raw, b.__raw); });
        out.refinements = refs;
        var orig = item.refine;
        out.refine = function (ref) {
          if (!ref || !ref.__aliases || ref.__aliases.length < 2 || !getHelper) return orig(ref);
          var h = getHelper();
          ref.__aliases.forEach(function (v) { if (h.state.isDisjunctiveFacetRefined(item.attribute, v)) h.removeDisjunctiveFacetRefinement(item.attribute, v); });
          h.search();
        };
        return out;
      });
      // facets in rail order; a facet without a rail container (e.g. the header's Inch | Metric toggle) keeps its place
      // relative to the others by going first (it sits above the rail); ties keep InstantSearch's order (stable)
      var rank = railRank();
      var keyed = out.map(function (it, i) { var r = rank[it.attribute]; return { it: it, i: i, r: r === undefined ? -1 : r }; });
      keyed.sort(function (a, b) { return (a.r - b.r) || (a.i - b.i); });
      return keyed.map(function (k) { return k.it; });
    };
  }

  return {
    version: '2026-10-10-p1r5c-batchC',
    esc: esc,
    // §1
    G: G, parseSize: parseSize, compareSize: compareSize, compareGrade: compareGrade, compareSeries: compareSeries, sortItems: sortItems,
    // §2
    canonical: canonical, mergeItems: mergeItems, normFind: normFind, normSize: normSize, itemMatches: itemMatches, bareNumber: bareNumber,
    systemOf: systemOf, splitSystems: splitSystems, systemBlocks: systemBlocks, decimalHint: decimalHint, inchMarks: inchMarks,
    // §3
    facetWidget: facetWidget, refineAliases: refineAliases, optionHtmlTile: optionHtmlTile, initTips: initTips,
    // §4
    TYPE_FAMILY: TYPE_FAMILY, familyOfType: familyOfType, familyFromHandle: familyFromHandle, detectFamily: detectFamily,
    FAMILIES: FAMILIES, GLOBAL_HIDE: GLOBAL_HIDE, railPlan: railPlan, Rail: Rail,
    // §5-§7
    unitToggle: unitToggle, SORTS: SORTS, sortControl: sortControl, drawer: drawer, showCountLabel: showCountLabel, isMobile: isMobile,
    // §8-§9
    money: money, packLabelOfHit: packLabelOfHit, packLineHtml: packLineHtml, fillPPP: fillPPP, chipsTransform: chipsTransform
  };
});
